import React, {useCallback, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {Alert, Button, Input, InputNumber, Modal, Radio, Table, Tag, Tooltip, Upload, message} from 'antd'
import {importTimesheet, previewTimesheetImport} from '../../../../services/api/apiProductionPlanning'
import {
    filterRows,
    importBlockerOf,
    isEditable,
    mergeEdits,
    overridePayloadOf,
    overwriteRowsOf,
    rowKeyOf,
    searchTimesheetRows,
    shiftCellTextOf,
    skipGroupsOf,
    warningTextOf,
} from '../../helpers/timesheetImport'
import {parseHoursInput} from '../../helpers/actualHours'
import {ACTUAL_HOURS_MAX} from '../../constants'
import {errorMessage} from '../../helpers/apiError'
import {DASH, formatDecimal, formatInt} from '../../helpers/format'

const {TextArea} = Input

/**
 * Số dòng một trang. Khai thành hằng vì ô `current` phải kẹp lại theo chính con số này — xem chỗ
 * dùng ở `<Table>`.
 */
const PAGE_SIZE = 20

/**
 * Chế độ xem MẶC ĐỊNH là "Nhập được", không phải "Tất cả".
 *
 * Bảng này có tới 1.500 dòng mà quá nửa là dòng không nhập được (mã nghỉ, ô trống, mã chưa có tài
 * khoản). Mở ra ở "Tất cả" thì màn hình đầu tiên người dùng thấy gần như toàn dòng xám — đúng phần
 * KHÔNG phải thứ họ sắp ghi. Các khối cảnh báo phía trên đã nói đủ về phần bị loại, và nút lọc vẫn
 * còn đó để xem lại.
 *
 * Dòng ghi đè nằm TRONG tập này (ghi đè là một dòng nhập được đã có giá trị cũ), nên mặc định này
 * phủ cả hai thứ người dùng cần soát trước khi bấm Nhập.
 */
const DEFAULT_MODE = 'importable'

/**
 * Bộ so sánh một cột chữ, theo bảng chữ cái tiếng Việt.
 *
 * ⚠️ Không cột nào được sort SẴN: thứ tự ban đầu là thứ tự dòng trong file, thứ duy nhất đối chiếu
 *    được với Excel đang mở bên cạnh. Sort sẵn một cột là lấy mất mốc đó ngay từ lúc mở.
 */
const byText = (field) => (a, b) => String(a[field] || '').localeCompare(String(b[field] || ''), 'vi')

/**
 * Giá trị sắp xếp của một ô ca. Ô trống (và ô mã nghỉ) dồn về một đầu bảng, thành một khối liền.
 *
 * ⚠️ KHÔNG quy ô trống về `0`: `0` là một giá trị THẬT ("ca đó làm 0 giờ"), nên quy về 0 là trộn
 *    lẫn ô HR không điền với ca thật sự 0 giờ — đúng hai thứ người soát đang cần tách ra.
 *    `-Infinity` là giá trị duy nhất không bao giờ trùng một con số có thật.
 *
 * antd không truyền chiều sắp xếp vào `sorter`, nên không ghim được ô trống xuống cuối ở CẢ hai
 * chiều; chúng nằm đầu khi tăng dần, cuối khi giảm dần. Chấp nhận: điều quan trọng là chúng liền
 * một khối chứ không rải giữa các con số.
 */
const shiftSortValue = (row, shift) => {
    const hours = shift === 'night' ? row.nightHours : row.dayHours

    return hours === null || hours === undefined ? -Infinity : hours
}

/**
 * Nhập giờ làm thực tế từ **bảng chấm công tổng hợp** của HR (FAC-26417 FR-001 bước 8).
 *
 * Hai bước, không có bước thứ ba: chọn file → xem trước → Nhập. Bản xem trước là thứ quan trọng
 * nhất của màn này, không phải nút Nhập.
 *
 * ⚠️ **Báo ghi đè TRƯỚC khi ghi, không phải sau.** Ô sắp bị ghi đè hiện rõ giá trị cũ → mới và lọc
 *    riêng ra được. Báo sau khi đã ghi thì người dùng chỉ còn cách đi sửa tay từng ô.
 *
 * ⚠️ **Dòng không nhập được phải NÓI RA, không giấu.** Lý do lớn nhất là mã nghỉ (`NL`, `P` — 162
 *    dòng trong file tháng 9) và nó KHÔNG phải lỗi phần mềm. Mỗi dòng bị bỏ là một ngày bị loại
 *    khỏi hiệu suất, nên giấu đi là để người dùng tin rằng đã nhập đủ.
 *
 * ⚠️ Con số trong bảng HR là **giờ công theo chế độ, chặn ở 8 giờ/ngày** — không phải giờ có mặt.
 *    Đây là điều quyết định cách hiểu mọi chỉ số hiệu suất dựng trên lượt nhập này; xem
 *    `src/domain/timesheet.ts` phía service để biết số đo và nó bỏ sót cái gì.
 *
 *    Màn này CỐ Ý không nhắc lại (gỡ 28/10/2026, theo yêu cầu): người đi nhập file không phải người
 *    đọc hiệu suất, và một khối cảnh báo cố định đứng trên mọi lượt nhập là thứ bị lướt qua sau lần
 *    thứ hai. Câu đó nay nằm ở nơi người ta thật sự đọc con số — màn Giờ làm thực tế và tooltip cột
 *    Hiệu suất (`constants/glossary.js`). Đừng thêm lại ở đây mà không bỏ ở hai chỗ kia.
 *
 * ⚠️ KHÔNG hỏi lý do thay đổi dù lượt nhập có ghi đè: service tự sinh lý do kèm tên file, tài khoản
 *    và mốc thời gian — tra ngược được hơn hẳn một câu gõ tay. Ô ghi chú dưới đây chỉ là tuỳ chọn.
 */
function TimesheetImportModal({visible, onClose, onImported}) {
    const [file, setFile] = useState(null)
    const [preview, setPreview] = useState(null)
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState(null)
    const [mode, setMode] = useState(DEFAULT_MODE)
    const [page, setPage] = useState(1)
    const [search, setSearch] = useState('')
    const [note, setNote] = useState('')
    /** Giờ người dùng gõ tay, khoá theo `rowKeyOf` (mã NV + ngày). Ô trống = quay lại giá trị của file. */
    const [edits, setEdits] = useState({})
    const [editErrors, setEditErrors] = useState({})

    const reset = useCallback(() => {
        setFile(null)
        setPreview(null)
        setError(null)
        setMode(DEFAULT_MODE)
        setPage(1)
        setSearch('')
        setNote('')
        setEdits({})
        setEditErrors({})
    }, [])

    const onPick = useCallback(async (picked) => {
        setFile(picked)
        setPreview(null)
        setError(null)
        setMode(DEFAULT_MODE)
        setPage(1)
        setSearch('')
        setEdits({})
        setEditErrors({})
        setLoading(true)

        try {
            setPreview(await previewTimesheetImport(picked))
        } catch (e) {
            setError(errorMessage(e) || 'Không đọc được bảng chấm công')
        } finally {
            setLoading(false)
        }
    }, [])


    const skipGroups = useMemo(() => skipGroupsOf(preview), [preview])
    /*
     * Hợp nhất giá trị gõ tay NGAY sau bản xem trước và trước mọi phép đếm. Đếm trên dữ liệu gốc
     * rồi hiển thị dữ liệu đã sửa là hai con số cãi nhau trên cùng một màn hình: nút Nhập nói 898
     * trong khi bảng vừa có thêm 12 dòng xanh.
     */
    const scaled = useMemo(() => (preview && preview.rows) || [], [preview])
    const allRows = useMemo(() => mergeEdits(scaled, edits), [scaled, edits])
    const scope = useMemo(() => filterRows(allRows, mode), [allRows, mode])
    const rows = useMemo(() => searchTimesheetRows(scope, search), [scope, search])
    const overwrites = useMemo(() => overwriteRowsOf(allRows).length, [allRows])
    const overrides = useMemo(() => overridePayloadOf(scaled, edits), [scaled, edits])
    const importableCount = useMemo(() => allRows.filter((r) => r.status === 'ok').length, [allRows])
    const warnCount = useMemo(() => allRows.filter((r) => (r.warnings || []).length > 0).length, [allRows])
    const noAccountCount = useMemo(() => allRows.filter((r) => r.status === 'no_account').length, [allRows])
    const invalidCount = Object.keys(editErrors).length

    const blocker = invalidCount > 0
        ? `Còn ${formatInt(invalidCount)} ô nhập sai, sửa xong mới nhập được.`
        : importBlockerOf(preview && {...preview, summary: {...preview.summary, importable: importableCount}})

    const onSubmit = useCallback(async () => {
        setSaving(true)

        try {
            const result = await importTimesheet(file, note.trim() || undefined, {overrides})

            message.success(
                `Đã nhập ${formatInt(result.created + result.updated)} ô giờ` +
                    (result.skipped > 0 ? ` · bỏ qua ${formatInt(result.skipped)} dòng` : '')
            )
            reset()
            onImported()
            onClose()
        } catch (e) {
            // Giữ nguyên bản xem trước khi lỗi: bắt chọn lại file và chờ đọc lại 1.500 dòng chỉ vì
            // một lỗi mạng là phạt người dùng cho một việc không phải của họ.
            message.error(errorMessage(e) || 'Không nhập được. Vui lòng thử lại')
        } finally {
            setSaving(false)
        }
    }, [file, note, overrides, onClose, onImported, reset])

    /**
     * Ghi nhận một lần gõ.
     *
     * ⚠️ Ô RỖNG không phải 0 giờ — `Number('')` ra `0`, đúng cái bẫy đã sập ở FAC-25605. Gõ rồi xoá
     *    đi phải trả dòng về đúng giá trị của file, chứ không biến nó thành "đã khai: hôm đó không
     *    làm". `parseHoursInput` là nơi duy nhất biết luật đó, dùng chung với lưới nhập tay.
     */
    const onEdit = useCallback((key, raw) => {
        const parsed = parseHoursInput(raw)

        setEditErrors((current) => {
            const next = {...current}

            if (parsed.ok) delete next[key]
            else next[key] = parsed.error

            return next
        })

        if (!parsed.ok) return

        setEdits((current) => {
            const next = {...current}

            if (parsed.value === null) delete next[key]
            else next[key] = parsed.value

            return next
        })
    }, [])

    /*
     * Đổi bộ lọc hay từ khoá thì về TRANG 1. antd 4 không tự lùi trang khi `dataSource` ngắn đi:
     * đang ở trang 12 của 1.500 dòng mà lọc còn 30 dòng thì bảng hiện RỖNG, và người dùng đọc ra
     * "bộ lọc không hoạt động" chứ không đọc ra "đang đứng sai trang".
     */
    const changeMode = useCallback((next) => {
        setMode(next)
        setPage(1)
    }, [])

    const changeSearch = useCallback((next) => {
        setSearch(next)
        setPage(1)
    }, [])

    const columns = [
        {
            /*
             * Số hàng trong Excel của NGƯỜI, không phải của dòng xem trước — mọi ngày của một người
             * mang y hệt một số. Nói ra ở tiêu đề: thấy "11" lặp chín lần liên tiếp mà không ai
             * giải thích thì người soát đọc ra là bảng bị lỗi.
             */
            title: <Tooltip title="Số hàng của người này trong file Excel — mọi ngày của họ cùng một hàng">Hàng</Tooltip>,
            dataIndex: 'row',
            width: 64,
            align: 'right',
            sorter: (a, b) => a.row - b.row,
        },
        {title: 'Mã NV', dataIndex: 'employeeCode', width: 84, sorter: byText('employeeCode')},
        {
            title: 'Tài khoản',
            dataIndex: 'actor',
            width: 170,
            sorter: byText('actor'),
            render: (actor, row) => {
                // Mã có hai tài khoản thì NÓI RA cả hai: đó chính là lý do dòng bị loại, và người
                // dùng cần biết phải đi đóng hiệu lực cái nào.
                if ((row.actors || []).length > 1) {
                    return <span className="VcMuted">{row.actors.join(' · ')}</span>
                }

                return actor ? <span className="VcHoursActor">{actor}</span> : <span className="VcMuted">{DASH}</span>
            },
        },
        {title: 'Ngày', dataIndex: 'workDate', width: 110, sorter: byText('workDate')},
        /*
         * Hai cột khớp đúng hai ô của file Excel, thay cho một cột gộp kiểu `3 + 5`. Cột gộp buộc
         * phải dán thêm chú thích "(có ca đêm)" để đọc được — tức tự nó không đọc được — và không
         * sắp xếp hay quét mắt theo ca được. Ô không có gì hiện gạch ngang, không hiện 0: `0` là
         * một giá trị THẬT (ca đó làm 0 giờ), khác hẳn ô HR không điền.
         */
        ...['day', 'night'].map((shift) => ({
            title: shift === 'day' ? 'Ca ngày' : 'Ca đêm',
            key: `shift-${shift}`,
            width: 86,
            align: 'right',
            sorter: (a, b) => shiftSortValue(a, shift) - shiftSortValue(b, shift),
            render: (text, row) => {
                const value = shiftCellTextOf(row, shift)

                return <span className={value === DASH ? 'VcMuted' : 'VcShiftCell'}>{value}</span>
            },
        })),
        {
            title: 'Giờ làm',
            key: 'working',
            width: 190,
            align: 'right',
            sorter: (a, b) => (a.hours === null ? -1 : a.hours) - (b.hours === null ? -1 : b.hours),
            render: (text, row) => {
                const key = rowKeyOf(row)
                const error = editErrors[key]
                const editable = isEditable(row)
                const net = row.hours

                return (
                    <div className={`VcHoursEditCell${row.manual ? ' VcHoursEditCell--manual' : ''}`}>
                        {/*
                          * Ghi đè hiện CẢ HAI giá trị. Chỉ hiện giá trị mới thì người dùng không
                          * biết mình vừa thay cái gì, và con số cũ không lấy lại được sau khi ghi.
                          */}
                        {row.currentHours !== null && row.currentHours !== undefined && (
                            <span className="VcOverwrite__from">{formatDecimal(row.currentHours, 1)}</span>
                        )}

                        {editable ? (
                            <InputNumber
                                className="VcHoursInput"
                                size="small"
                                /*
                                 * `null` phải thành `undefined`: antd hiện `null` thành chuỗi rỗng
                                 * đúng như mong muốn nhưng coi nó là giá trị ĐÃ ĐẶT, nên ô mất
                                 * `placeholder` — mất luôn dấu hiệu phân biệt "chưa có giờ" với
                                 * "đã khai 0".
                                 */
                                value={net === null || net === undefined ? undefined : net}
                                min={0}
                                max={ACTUAL_HOURS_MAX}
                                step={0.25}
                                /*
                                 * ⚠️ PHẢI khai `precision`. `rc-input-number` suy số chữ số thập
                                 *    phân từ CHUỖI của `step` rồi `toFixed` theo đó khi ô mất
                                 *    focus; để nó tự suy là có lúc cột hiện `8,000000000000000000`.
                                 *
                                 * Hai chữ số vì bảng HR có ô lẻ tới phần tư giờ (`7.75`, `2.25`).
                                 */
                                precision={2}
                                // Người nhập ở xưởng gõ `7,5`; cả màn hình hiển thị số theo vi-VN.
                                decimalSeparator=","
                                placeholder={DASH}
                                onChange={(raw) => onEdit(key, raw)}
                            />
                        ) : (
                            <span className="VcMuted">
                                {net === null || net === undefined ? DASH : formatDecimal(net, 1)}
                            </span>
                        )}

                        {error && <div className="VcHoursCell__error">{error}</div>}
                    </div>
                )
            },
        },
        {
            title: 'Kết quả',
            dataIndex: 'status',
            sorter: byText('status'),
            render: (status, row) => {
                const labels = (preview && preview.labels) || {}
                const warning = warningTextOf(row)

                if (status !== 'ok') {
                    return (
                        <span className="VcSkipReason">
                            {labels[status] || status}
                            {isEditable(row) && (
                                <span className="VcHint"> Gõ số giờ vào ô bên trái để vẫn nhập được dòng này.</span>
                            )}
                        </span>
                    )
                }

                // Màu không phải kênh duy nhất (AC-UI-01): lý do luôn là CHỮ, thẻ màu chỉ đi kèm.
                if (row.manual) return <Tag color="blue">Sửa tay</Tag>

                return warning
                    ? <span className="VcWarnReason"><Tag color="orange">Kiểm lại</Tag>{warning}</span>
                    : <Tag color="green">Nhập được</Tag>
            },
        },
    ]

    return (
        <Modal
            title="Nhập giờ từ file chấm công"
            visible={visible}
            width={1320}
            onCancel={() => {
                reset()
                onClose()
            }}
            footer={[
                <Button key="cancel" onClick={() => { reset(); onClose() }}>Đóng</Button>,
                <Button
                    key="ok"
                    type="primary"
                    disabled={!!blocker || loading}
                    loading={saving}
                    onClick={onSubmit}
                >
                    {importableCount > 0 ? `Nhập ${formatInt(importableCount)} ô giờ` : 'Nhập'}
                </Button>,
            ]}
        >
            <div className="VcTimesheetImport">
                <Upload
                    accept=".xlsx"
                    showUploadList={false}
                    // `beforeUpload` trả false: antd không tự POST đi đâu cả, file chỉ nằm lại ở
                    // client cho tới khi ta gọi endpoint preview.
                    beforeUpload={(picked) => {
                        onPick(picked)

                        return false
                    }}
                >
                    <Button loading={loading}>{file ? `Đổi file (${file.name})` : 'Chọn file .xlsx'}</Button>
                </Upload>

                <div className="VcHint">
                    Bảng chấm công tổng hợp của HR (sheet <strong>CC</strong>), một dòng một người,
                    hai cột mỗi ngày (<strong>Ngày</strong> · <strong>Đêm</strong>).
                </div>

                {error && <Alert type="error" showIcon message={error} className="VcMt8"/>}

                {preview && (
                    <>
                        <div className="VcImportStats">
                            <div className="VcImportStat VcImportStat--ok">
                                <div className="VcImportStat__value">{formatInt(importableCount)}</div>
                                <div className="VcImportStat__label">Nhập được / {formatInt(preview.readRows)} dòng</div>
                            </div>
                            <div className="VcImportStat">
                                <div className="VcImportStat__value">{formatInt(overwrites)}</div>
                                <div className="VcImportStat__label">Ô sẽ bị ghi đè</div>
                            </div>
                            <div className="VcImportStat">
                                <div className="VcImportStat__value">
                                    {preview.from ? `${preview.from} → ${preview.to}` : DASH}
                                </div>
                                <div className="VcImportStat__label">Khoảng ngày trong file</div>
                            </div>
                        </div>

                        {skipGroups.length > 0 && (
                            <Alert
                                type="warning"
                                showIcon
                                className="VcMt8"
                                message={`${formatInt(preview.readRows - importableCount)} dòng KHÔNG nhập được`}
                                description={
                                    <>
                                        <ul className="VcSkipList">
                                            {skipGroups.map((group) => (
                                                <li key={group.status}>
                                                    <strong>{formatInt(group.count)}</strong> ({group.share}%) —{' '}
                                                    {group.label}
                                                </li>
                                            ))}
                                        </ul>
                                        {/*
                                          * Nói thẳng hệ quả. Dòng bị bỏ KHÔNG được ghi 0 giờ: 0 nghĩa
                                          * là "đã khai hôm đó không làm" và ngày đó vào mẫu số, còn
                                          * không có dữ liệu thì ngày đó bị loại khỏi hiệu suất.
                                          */}
                                        <div className="VcHint">
                                            Những dòng này không được ghi 0 giờ — ô trống nghĩa là
                                            “chưa nhập” và ngày đó bị loại khỏi hiệu suất, khác hẳn 0
                                            nghĩa là “đã khai hôm đó không làm”. Muốn tính thì nhập tay
                                            trên lưới.
                                        </div>
                                    </>
                                }
                            />
                        )}

                        {(preview.issues || []).length > 0 && (
                            <Alert
                                type="error"
                                showIcon
                                className="VcMt8"
                                message={`${formatInt(preview.issues.length)} lỗi đọc file`}
                                description={
                                    <ul className="VcSkipList">
                                        {preview.issues.slice(0, 10).map((issue) => (
                                            <li key={`${issue.row}-${issue.code}`}>
                                                Dòng {issue.row}: {issue.message}
                                            </li>
                                        ))}
                                    </ul>
                                }
                            />
                        )}

                        <div className="VcPreviewTools">
                            <Radio.Group
                                value={mode}
                                size="small"
                                onChange={(e) => changeMode(e.target.value)}
                            >
                                <Radio.Button value="importable">
                                    Nhập được ({formatInt(importableCount)})
                                </Radio.Button>
                                <Radio.Button value="overwrite">Ghi đè ({formatInt(overwrites)})</Radio.Button>
                                <Radio.Button value="warning">
                                    Cần kiểm ({formatInt(warnCount)})
                                </Radio.Button>
                                {/*
                                  * Nhóm mã chưa có tài khoản tách riêng khỏi "Bị bỏ": đây là nhóm
                                  * DUY NHẤT người dùng tự gỡ được (bổ sung bản đồ tài khoản rồi
                                  * nhập lại file này), còn mã nghỉ và ô trống là hiện trạng bảng HR.
                                  *
                                  * Chỉ hiện khi CÓ dòng như vậy — một nút lọc luôn đứng đó với số 0
                                  * là dạy người dùng lướt qua cả hàng nút.
                                  */}
                                {noAccountCount > 0 && (
                                    <Radio.Button value="no_account">
                                        Chưa có tài khoản ({formatInt(noAccountCount)})
                                    </Radio.Button>
                                )}
                                <Radio.Button value="skipped">
                                    Bị bỏ ({formatInt(preview.readRows - importableCount)})
                                </Radio.Button>
                                <Radio.Button value="all">Tất cả ({formatInt(preview.readRows)})</Radio.Button>
                            </Radio.Group>

                            {/*
                              * Bảng này tới 2.000 dòng: không có ô tìm thì xem lại một người cụ thể
                              * nghĩa là lật 100 trang. Ngày gõ theo đúng dạng đang hiện (`2026-07-02`).
                              */}
                            <Input.Search
                                size="small"
                                allowClear
                                style={{maxWidth: 300}}
                                value={search}
                                placeholder="Tìm mã NV, tài khoản, ngày…"
                                onChange={(e) => changeSearch(e.target.value)}
                            />

                            {search && (
                                <span className="VcMuted">
                                    {formatInt(rows.length)}/{formatInt(scope.length)} dòng
                                </span>
                            )}
                        </div>

                        <Table
                            className="VcTable VcMt8"
                            /*
                              * ⚠️ Khoá là (mã nhân viên, ngày), KHÔNG phải `row`. `row` là số hàng
                              *    Excel của NGƯỜI nên 31 dòng của cùng một người mang y hệt một
                              *    số — React nhận trùng khoá, ô nhập nhảy sang dòng khác và bảng
                              *    không vẽ lại khi đổi bộ lọc. Xem `rowKeyOf`.
                              */
                            rowKey={rowKeyOf}
                            size="small"
                            columns={columns}
                            dataSource={rows}
                            // Giữ nguyên thứ tự dòng trong file — thứ duy nhất đối chiếu được với
                            // Excel đang mở bên cạnh. Không sort mặc định theo bất cứ cột nào.
                            pagination={{
                                /*
                                 * Kẹp lại theo số trang THẬT: sửa tay một dòng có thể đẩy nó sang
                                 * bộ lọc khác, và trang đang đứng biến mất mà không có sự kiện nào
                                 * báo. Không kẹp thì bảng hiện rỗng dù vẫn còn dòng.
                                 */
                                current: Math.min(page, Math.max(1, Math.ceil(rows.length / PAGE_SIZE))),
                                pageSize: PAGE_SIZE,
                                showSizeChanger: false,
                                size: 'small',
                                onChange: setPage,
                            }}
                            /*
                              * KHÔNG `scroll.y`. Bảng cuộn trong khi modal cũng cuộn là hai thanh
                              * cuộn lồng nhau: bánh xe chuột dừng ở thanh trong, và người dùng
                              * không ra được phần bên dưới bảng (ô ghi chú, nút Nhập) nếu con trỏ
                              * còn nằm trên bảng. Modal này đã tự cuộn rồi.
                              */
                            locale={{
                                emptyText: search
                                    ? 'Không dòng nào khớp từ khoá.'
                                    : 'Không có dòng nào trong mục này.',
                            }}
                        />

                        <div className="VcField VcMt8">
                            <label>Ghi chú (tuỳ chọn)</label>
                            <TextArea
                                rows={2}
                                value={note}
                                maxLength={300}
                                placeholder="Ví dụ: bù công tháng 7 sau khi chốt với bộ phận nhân sự"
                                onChange={(e) => setNote(e.target.value)}
                            />
                            <div className="VcHint">
                                Nhật ký đã tự ghi tên file, tài khoản và thời điểm nhập — ô này chỉ để
                                nói thêm vì sao.
                            </div>
                        </div>
                    </>
                )}

                {blocker && preview && <Alert type="warning" showIcon className="VcMt8" message={blocker}/>}
            </div>
        </Modal>
    )
}

TimesheetImportModal.propTypes = {
    visible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    /** Gọi sau khi ghi xong để màn cha tải lại lưới. */
    onImported: PropTypes.func.isRequired,
}

export default TimesheetImportModal
